import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signOut,
  User,
  authState,
  IdTokenResult,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

/**
 * Authentication service for handling Google authentication.
 * Provides methods for signing in, signing out, and observing auth state.
 * Implements token caching with security mitigations (30-second TTL, force refresh on critical events).
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private router = inject(Router);
  private injector = inject(Injector);
  private googleProvider = new GoogleAuthProvider();
  private microsoftProvider = new OAuthProvider('microsoft.com');

  // Token result cache with TTL for security
  private tokenResultCache: {
    result: IdTokenResult;
    timestamp: number;
    userId: string;
    tenantId?: string; // Track tenantId in cache for validation
  } | null = null;
  private readonly TOKEN_CACHE_TTL = 30000; // 30 seconds (short TTL for security)

  constructor() {
    // Always prompt for account selection
    this.googleProvider.setCustomParameters({
      prompt: 'select_account',
    });
    this.microsoftProvider.setCustomParameters({
      prompt: 'select_account',
    });
  }

  /**
   * Observable of the current authentication state
   */
  authState$: Observable<User | null> = authState(this.auth);

  /**
   * Get the current user
   */
  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  /**
   * Sign in with Google
   * On web: uses signInWithPopup. On native (Capacitor): uses native Google Sign-In plugin then signInWithCredential.
   * Clears token cache on sign-in to ensure fresh claims.
   */
  async signInWithGoogle(): Promise<void> {
    try {
      const useNative = await this.isNativePlatform();
      if (useNative) {
        await this.signInWithGoogleNative();
      } else {
        await runInInjectionContext(this.injector, async () => {
          await signInWithPopup(this.auth, this.googleProvider);
        });
      }
      this.clearTokenCache();
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  /**
   * Sign in with Microsoft using Firebase's Microsoft OAuth provider.
   * Clears token cache on sign-in to ensure fresh claims.
   */
  async signInWithMicrosoft(): Promise<void> {
    try {
      await runInInjectionContext(this.injector, async () => {
        await signInWithPopup(this.auth, this.microsoftProvider);
      });
      this.clearTokenCache();
    } catch (error) {
      console.error('Microsoft sign in error:', error);
      throw error;
    }
  }

  /**
   * True when running inside a native Capacitor app (Android/iOS), so we use native Google Sign-In.
   * Using isNativePlatform() avoids ever using signInWithPopup in the app (which can open browser and crash).
   */
  private async isNativePlatform(): Promise<boolean> {
    try {
      const { Capacitor } = await import('@capacitor/core');
      return Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  }

  /** Web client ID for native Google Sign-In (must match Firebase/Google OAuth Web client). Used to initialize the plugin. */
  private static readonly NATIVE_GOOGLE_WEB_CLIENT_ID =
    '376131366176-ht8e5g8hfqkb1nsc916ukqblsifvrjl4.apps.googleusercontent.com';

  private async signInWithGoogleNative(): Promise<void> {
    const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
    try {
      await GoogleAuth.initialize({
        clientId: AuthService.NATIVE_GOOGLE_WEB_CLIENT_ID,
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });
    } catch (initErr) {
      console.error('Google Sign-In initialize error:', initErr);
    }
    let googleUser: { authentication?: { idToken?: string } } | null = null;
    try {
      googleUser = await GoogleAuth.signIn();
    } catch (err) {
      console.error('Google Sign-In plugin error:', err);
      throw new Error('Sign-in was cancelled or failed. Please try again.', {
        cause: err,
      });
    }
    const idToken = googleUser?.authentication?.idToken;
    if (!idToken) {
      throw new Error('Sign-in was cancelled or did not complete. Please try again.');
    }
    const credential = GoogleAuthProvider.credential(idToken);
    await runInInjectionContext(this.injector, async () => {
      await signInWithCredential(this.auth, credential);
    });
  }

  /**
   * Sign out the current user
   * Clears token cache on sign-out
   */
  async signOut(): Promise<void> {
    try {
      // Ensure Firebase API call is made within Angular's injection context
      await runInInjectionContext(this.injector, async () => {
        await signOut(this.auth);
      });
      // Clear cache on sign-out
      this.clearTokenCache();
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.auth.currentUser !== null;
  }

  /**
   * Get the current user's ID token for API authentication
   */
  async getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) {
      return null;
    }
    try {
      // Ensure Firebase API call is made within Angular's injection context
      return await runInInjectionContext(this.injector, async () => {
        return await user.getIdToken();
      });
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  /**
   * Get token result with caching (30-second TTL)
   * Force refresh on critical events (login, tenant selection, etc.)
   * @param forceRefresh - Force refresh token even if cache is valid
   * @param expectedTenantId - Optional tenant ID to validate against cached token
   */
  async getTokenResult(
    forceRefresh: boolean = false,
    expectedTenantId?: string | null,
  ): Promise<IdTokenResult | null> {
    const user = this.auth.currentUser;
    if (!user) {
      return null;
    }

    try {
      // Check if cache is valid and user hasn't changed
      let cacheValid =
        !forceRefresh &&
        this.tokenResultCache !== null &&
        this.tokenResultCache.userId === user.uid &&
        Date.now() - this.tokenResultCache.timestamp < this.TOKEN_CACHE_TTL;

      // If we have expected tenant, validate cache matches
      if (cacheValid && this.tokenResultCache !== null) {
        if (expectedTenantId !== undefined && expectedTenantId !== null) {
          const cachedTenantId = this.tokenResultCache.tenantId;
          if (cachedTenantId !== expectedTenantId) {
            // Cache has wrong tenant - invalidate it
            cacheValid = false;
          }
        }
      }

      if (cacheValid && this.tokenResultCache !== null) {
        return this.tokenResultCache.result;
      }

      // Ensure Firebase API call is made within Angular's injection context
      // Only force refresh if explicitly requested (critical events)
      // Otherwise, getIdTokenResult() will refresh if token is expired
      const result = await runInInjectionContext(this.injector, async () => {
        return await user.getIdTokenResult(forceRefresh);
      });

      // If we have expected tenant, verify the token actually has it before caching
      // This prevents caching stale tokens after tenant activation
      if (expectedTenantId !== undefined && expectedTenantId !== null) {
        const claimTenantId = result.claims['tenantId'] as string | undefined;
        if (claimTenantId !== expectedTenantId) {
          // Token doesn't have expected tenant - don't cache it
          // Clear any existing cache to prevent serving stale tokens
          this.tokenResultCache = null;
          // Return it anyway, but caller should handle the mismatch
          return result;
        }
      }

      // Update cache with tenantId
      this.tokenResultCache = {
        result,
        timestamp: Date.now(),
        userId: user.uid,
        tenantId: result.claims['tenantId'] as string | undefined,
      };

      return result;
    } catch (error) {
      console.error('Error getting token result:', error);
      return null;
    }
  }

  /**
   * Clear the token result cache
   * Should be called on critical events: sign-in, sign-out, tenant selection
   */
  clearTokenCache(): void {
    this.tokenResultCache = null;
  }

  /**
   * Get the current user's tenant ID from custom claims
   * Uses cached token result when possible (30-second TTL)
   */
  async getTenantId(forceRefresh: boolean = false): Promise<string | null> {
    const tokenResult = await this.getTokenResult(forceRefresh);
    if (!tokenResult) {
      return null;
    }
    return (tokenResult.claims['tenantId'] as string) || null;
  }

  /**
   * Get the current user's Firestore document ID from custom claims
   * Uses cached token result when possible (30-second TTL)
   */
  async getUserDocumentId(
    tenantId?: string | null,
    forceRefresh: boolean = false,
  ): Promise<string | null> {
    const tokenResult = await this.getTokenResult(forceRefresh);
    if (!tokenResult) {
      return null;
    }

    const claims = tokenResult.claims;
    const directUserId = claims['userId'] as string | undefined;
    if (directUserId) {
      return directUserId;
    }

    const tenantUserIds = claims['tenantUserIds'] as Record<string, string> | undefined;
    if (tenantId && tenantUserIds && tenantUserIds[tenantId]) {
      return tenantUserIds[tenantId];
    }

    return null;
  }

  /**
   * Get the current user's role from custom claims
   * Uses cached token result when possible (30-second TTL)
   */
  async getUserRole(forceRefresh: boolean = false): Promise<string | null> {
    const tokenResult = await this.getTokenResult(forceRefresh);
    if (!tokenResult) {
      return null;
    }
    return tokenResult.claims['role'] as string | null;
  }

  /**
   * Check if the current user has a specific role
   */
  async hasRole(role: string, forceRefresh: boolean = false): Promise<boolean> {
    const userRole = await this.getUserRole(forceRefresh);
    return userRole === role;
  }

  /**
   * Get all custom claims from token result
   * Uses cached token result when possible (30-second TTL)
   */
  async getCustomClaims(forceRefresh: boolean = false): Promise<IdTokenResult['claims'] | null> {
    const tokenResult = await this.getTokenResult(forceRefresh);
    if (!tokenResult) {
      return null;
    }
    return tokenResult.claims;
  }

  /**
   * Get a specific custom claim from token result
   * Uses cached token result when possible (30-second TTL)
   * @param claimName - Name of the custom claim to retrieve
   * @param forceRefresh - Force refresh token even if cache is valid
   * @returns The claim value, or null if not found
   */
  async getCustomClaim(
    claimName: string,
    forceRefresh: boolean = false,
  ): Promise<boolean | string | any[] | Record<string, any> | null> {
    const tokenResult = await this.getTokenResult(forceRefresh);
    if (!tokenResult) {
      return null;
    }
    return (tokenResult.claims[claimName] as any) || null;
  }

  isSignInCancelledError(error: unknown): boolean {
    const { code, message } = this.getAuthErrorParts(error);
    return (
      code.includes('popup-closed-by-user') ||
      code.includes('cancelled-popup-request') ||
      code.includes('user-cancelled') ||
      code.includes('user_cancelled') ||
      message.includes('popup closed') ||
      message.includes('cancelled') ||
      message.includes('canceled')
    );
  }

  isUnauthorizedSignInError(error: unknown): boolean {
    const { code, message } = this.getAuthErrorParts(error);
    return (
      code.includes('-47') ||
      code.includes('error-code:-47') ||
      code.includes('unauthorized') ||
      code.includes('permission-denied') ||
      code.includes('user-disabled') ||
      message.includes('access denied') ||
      message.includes('not authorized') ||
      message.includes('unauthorized') ||
      message.includes('user not found') ||
      message.includes('contact your administrator') ||
      message.includes('only crowdctrl.app@gmail.com') ||
      message.includes('is authorized to access')
    );
  }

  getSignInErrorMessage(error: unknown): string {
    const { code, message, originalMessage } = this.getAuthErrorParts(error);

    if (this.isSignInCancelledError(error)) {
      return '';
    }

    if (this.isUnauthorizedSignInError(error)) {
      return 'Your account is not authorized for this app. Please contact your administrator.';
    }

    if (code.includes('popup-blocked')) {
      return 'The sign-in popup was blocked. Allow popups for this site and try again.';
    }
    if (code.includes('account-exists-with-different-credential')) {
      return 'An account already exists with the same email using a different sign-in method.';
    }
    if (code.includes('operation-not-allowed')) {
      return 'This sign-in method is not enabled yet. Please contact support.';
    }
    if (code.includes('invalid-credential') || code.includes('credential-already-in-use')) {
      return 'The sign-in credentials could not be used. Please try again.';
    }
    if (code.includes('network-request-failed')) {
      return 'Network error while signing in. Check your connection and try again.';
    }
    if (code.includes('too-many-requests')) {
      return 'Too many sign-in attempts. Please wait a moment and try again.';
    }
    if (code.includes('provider-already-linked')) {
      return 'This sign-in provider is already linked to another account.';
    }
    if (code.includes('auth-domain-config-required') || code.includes('unauthorized-domain')) {
      return 'Sign-in is not configured for this domain. Please contact support.';
    }
    if (code.includes('operation-not-supported-in-this-environment')) {
      return 'This sign-in method is not supported in this browser or app environment.';
    }
    if (code.includes('web-storage-unsupported')) {
      return 'Your browser is blocking required storage for sign-in. Enable browser storage and try again.';
    }
    if (code.includes('popup-redirect-cancelled-by-user')) {
      return '';
    }

    if (message.includes('microsoft') && message.includes('configuration')) {
      return 'Microsoft sign-in is not configured correctly. Please contact support.';
    }

    return originalMessage || 'Sign-in failed. Please try again.';
  }

  private getAuthErrorParts(error: unknown): {
    code: string;
    message: string;
    originalMessage: string;
  } {
    const errorObj = (error ?? {}) as { code?: unknown; message?: unknown };
    const code =
      typeof errorObj.code === 'string' || typeof errorObj.code === 'number'
        ? String(errorObj.code).toLowerCase()
        : '';
    const originalMessage = typeof errorObj.message === 'string' ? errorObj.message : '';
    return {
      code,
      message: originalMessage.toLowerCase(),
      originalMessage,
    };
  }
}
